/* eslint-disable import/prefer-default-export */
import * as events from '../events';
import genericEvents from './generic-events';
import Utilities from '../utilities';


export const FlowModule = {
    name: 'Login Bouygues',
    logger: {},
    dispatcher: {},
    uiControl: {},
    tracker: {},
    integrator: {},
    productUrl: '',
    locale: '',
    productSlug: '',
    plan: '',
    fraudDetection: '',
    internalCall: false,
    identification: {
        identity: '',
        identified: false,
        callFinished: false,
        timeout: 10,
        error: false,
    },
    territory: '',
    metadata: {},
    step: '',
    flowSteps: {
        INIT: 'init',
        REDIRECTING: 'redirecting',
        SPINNER_AFTER_SUBSCRIBE: 'spinner-subscribe',
        FRAUD: 'fraud',
        WIFI: 'wifi',
        SUBSCRIPTION_FAILED: 'subscription failed',
        SUBSCRIPTION_SUCCEEDED: 'subscription success',
        BAD_INTEGRATOR_REQUEST: 'bad_integrator_request',
    },
    pageStates: {
        REDIRECTING: 'redirecting',
        SPINNER_AFTER_SUBSCRIBE: 'spinner-subscribe',
        WIFI: 'wifi',
        ERROR: 'error',
        SUCCESS: 'success',
    },

    /**
     * Method to initialise the flow. We first check if the user was redirected to the LP from the
     * integrator and we deal with that accordingly, if not, we'll initialise a new flow
     */
    init() {
        this.integrator.appendBaseData({ territory: this.territory });
        this.setFlowStep(this.flowSteps.INIT);
        this.bindTrackingEvents();

        // set the LP url in the metadata
        this.metadata.lp_url = window.location.origin + window.location.pathname;
        this.metadata.fraud_detection_id = this.metadata.fraud_transaction_id;

        this.logger.debug(`Flow (${this.name}): initialising flow and checking for integrator requests`);

        this.initNewFlow();
    },

    /**
     * The user got here from an ad, so we're starting a new flow
     */
    initNewFlow() {
        this.logger.debug(`Flow (${this.name}): initialising new flow`);
        window.addEventListener('message', this.onIframeMessage.bind(this), false);
        if (!this.internalCall) {
            this.flowIdentification();
        }
    },

    /**
     * Set the current step of the flow
     *
     * @param {string} flowStep
     */
    setFlowStep(flowStep) {
        this.logger.debug(`Flow (${this.name}): changing step from ${this.step} to ${flowStep}`);
        this.step = flowStep;

        this.setPageStateForFlowStep(flowStep);
    },

    /**
     * Set the current state of the page
     *
     * @param {string} flowStep
     */
    setPageStateForFlowStep(flowStep) {
        switch (flowStep) {
            case this.flowSteps.INIT:
                break;
            case this.flowSteps.REDIRECTING:
                this.uiControl.setPageState(this.pageStates.REDIRECTING);
                break;
            case this.flowSteps.WIFI:
                this.uiControl.showWifiFlowWithRefreshAndLoginLink(this.getProductRedirectUrl());
                this.uiControl.hideElement(this.uiControl.controls.spinner);
                this.uiControl.hideElement(this.uiControl.controls.subscribe);
                this.uiControl.hideElement(this.uiControl.controls.confirm);
                this.uiControl.hideElement(this.uiControl.controls.slider.sliderImagesContainer)
                this.uiControl.setPageState(this.pageStates.WIFI);
                break;
            case this.flowSteps.FRAUD:
            case this.flowSteps.SUBSCRIPTION_FAILED:
            case this.flowSteps.BAD_INTEGRATOR_REQUEST:
            default:
                this.uiControl.setPageState(this.pageStates.ERROR);
                break;
        }
    },

    /**
     * Binding the tracking events
     */
    bindTrackingEvents() {
        this.dispatcher.addEventListener(events.FRAUD_DETECTED, (event) => {
            this.tracker.track(events.FRAUD_DETECTED, {
                data: {
                    reference: event.reference,
                    status_code: event.status_code,
                },
            });
        });

        this.dispatcher.addEventListener(events.SUBSCRIPTION_FAILED, (event) => {
            this.tracker.track(events.SUBSCRIPTION_FAILED, {
                data: {
                    reference: event.reference,
                    status_code: event.status_code,
                },
            });
        });

        this.dispatcher.addEventListener(events.REDIRECTING, (event) => {

            const urlParams = new URLSearchParams(window.location.search);
            const fromLpLogin = urlParams.get('fromLpLogin') ? "true" : "false";
            this.tracker.track(events.REDIRECTING, {
                data: {
                    url: event.url,
                    status_code: event.status_code,
                    from_lp_login: fromLpLogin
                },
            });
            // remove unload message
            window.onbeforeunload = null;
            // Redirect the user
            this.logger.debug(`Flow (${this.name}): redirecting user`, event.url);
            window.location.assign(event.url);
        });

        this.dispatcher.addEventListener(events.SUBSCRIPTION_SUCCEEDED, (event) => {
            this.tracker.track(events.SUBSCRIPTION_SUCCEEDED, {
                data: {
                    subscription: event.jwt,
                    reference: event.reference,
                },
            });

            const redirector = () => {
                this.redirectToProduct(event.jwt);
            };

            this.uiControl.showElement(this.uiControl.controls.success.container);
            this.uiControl.controls.success.continue.addEventListener('click', redirector.bind(this));
            window.setTimeout(redirector.bind(this), 5000);
        });
    },

    /**
     * Redirect to product with an auth token
     *
     * @param {string} jwt
     */
    redirectToProduct(jwt) {
        // remove unload message
        window.onbeforeunload = null;

        this.tracker.track(events.REDIRECT_TO_PRODUCT);

        const urlParams = new URLSearchParams(window.location.search);
        const fromLpLogin = urlParams.get('fromLpLogin') ? "true" : "false";
        this.tracker.track(events.REDIRECT_TO_PRODUCT, {
            data: {
                fromLpLogin: fromLpLogin
            }
        });

        let redirectUrl = this.productUrl;
        const queryConcat = (redirectUrl.indexOf('?') !== -1) ? '&' : '?';

        redirectUrl = `${this.productUrl}${queryConcat}auth=${jwt}&locale=${this.locale}&fromLpLogin=${fromLpLogin}`;

        this.logger.debug(`Flow (${this.name}): redirecting to product`, redirectUrl);

        window.location.assign(redirectUrl);
    },

    isIdentifyCallFinished() {
        return this.identification.callFinished;
    },

    /**
     * Start the identification process
     */
    flowIdentification() {
        this.tracker.track(events.IDENTIFY_CALLED);
        this.logger.debug(`Flow (${this.name}): identifying customer`);
        this.setFlowStep(this.flowSteps.spinner)

        setTimeout((() => {
            this.logger.debug(`Flow (${this.name}): identification timeout`);

            if (!this.identification.callFinished) {
                this.tracker.track(events.IDENTIFY_ERROR, {
                    data: {
                        error: `request timeout reached - ${this.identification.timeout}s`,
                    },
                });
                this.redirectToProductWithoutAuth()
            }
        }), this.identification.timeout * 1000);

        this.integrator.heIframeIdentify({
            return: window.location.origin + window.location.pathname,
            metadata: this.metadata,
        });
    },

    /**
     * Handler for iframe postMessages
     * @param {Object} event
     */
    onIframeMessage(event) {
        this.logger.info(`Flow (${this.name}): received iframe message`, event);

        const originDomain = event.origin.split('://');
        const integratorDomain = this.integrator.baseUrl.split('://');

        if (typeof originDomain[1] !== 'undefined' && originDomain[1] !== '' && originDomain[1] !== integratorDomain[1]) {
            this.logger.error(`Flow (${this.name}): failed to validate message origin.`, originDomain, integratorDomain);
            return;
        }

        const response = event.data;
        const trackingData = {
            identity: response.identity,
            identified: response.identified,
            reference: response.reference,
        };

        if (response.status === 'SUCCESS' && response.status_code === 200) {
            this.identification.identity = response.identity;
            this.identification.identified = response.identified;
            this.metadata.identity = response.identity;

            if (response.redirect) {
                this.setFlowStep(this.flowSteps.REDIRECTING);
                this.dispatcher.dispatchEvent(events.REDIRECTING, {
                    url: response.redirect.url,
                    status_code: response.redirect.code,
                });
            } else if (response.identified) {
                this.identification.callFinished = true;
                this.tracker.track(events.IDENTIFY_SUCCESS, { data: trackingData });
                this.redirectToSubscribingLandingPage()
            } else {
                this.setFlowStep(this.flowSteps.WIFI);
                this.identification.callFinished = true;
                this.tracker.track(events.IDENTIFY_FAILURE, { data: trackingData });
                this.dispatcher.dispatchEvent(events.IDENTIFY_FAILURE);
                // this.redirectToProductWithoutAuth()
            }
        } else { // 'ERROR'
            this.setFlowStep(this.flowSteps.WIFI);
            this.identification.error = true;
            this.identification.callFinished = true;
            this.tracker.track(events.IDENTIFY_ERROR, { data: trackingData });
            this.dispatcher.dispatchEvent(events.IDENTIFY_ERROR);
            // this.redirectToProductWithoutAuth()
        }
    },

    redirectToProductWithoutAuth() {//overridable
        // remove unload message
        window.onbeforeunload = null;
        const redirectUrl = this.getProductRedirectUrl(this.productUrl, '', {});

        this.logger.debug(`Flow (${this.name}): redirecting to product without auth`, redirectUrl);
        this.tracker.track(events.REDIRECT_TO_PRODUCT, {
            data: {
                redirectUrl: redirectUrl,
                withAuth: false,
            },
        });

        window.location.assign(redirectUrl);
    },

    redirectToSubscribingLandingPage() {
        // remove unload message
        window.onbeforeunload = null;
        const redirectUrl = this.metadata['page_attributes-subscribe_landing_page'];


        this.logger.info(`Flow (${this.name}): redirecting to LP`, redirectUrl);
        this.tracker.track(events.REDIRECT_TO_LANDING_PAGE, {
            data: {
                redirectUrl: redirectUrl,
                withAuth: false,
            },
        });

        window.location.assign(redirectUrl);
    },

    getProductRedirectUrl(baseUrl = this.productUrl) {
        const urlParams = new URLSearchParams(window.location.search);
        let redirectUrl;

        if (urlParams.has('returnUrl')) {
            redirectUrl = new URL(urlParams.get('returnUrl'));
        } else {
            redirectUrl = new URL(baseUrl);
            redirectUrl.pathname = 'login';
        }

        redirectUrl.searchParams.append('locale', this.locale);
        redirectUrl.searchParams.append('identified', '1');
        redirectUrl.searchParams.append('event_tracking_id', this.metadata.event_tracking_id);

        return redirectUrl;
    },

};

window.FlowModule = FlowModule;
